// src/components/useAuth.js
import { useState, useEffect, useContext, createContext } from "react";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updatePassword,
  fetchSignInMethodsForEmail,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithCustomToken,
} from "firebase/auth";

// Create a context for authentication
const AuthContext = createContext(null);

// Provider component that wraps your app and makes auth object available
export function AuthProvider({ children }) {
  const auth = useProvideAuth();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

// Hook for components to get auth object
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider hook that creates auth object and handles state
function useProvideAuth() {
  const [user, setUser] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null); // Full Firebase user object
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const auth = getAuth();

  // E2E test auth bypass — only active when REACT_APP_E2E_AUTH_BYPASS=true at build time.
  // CRA inlines process.env.REACT_APP_* at build, so this is dead-code-eliminated in production.
  useEffect(() => {
    if (process.env.REACT_APP_E2E_AUTH_BYPASS === "true") {
      try {
        const raw = sessionStorage.getItem("e2e_test_user");
        if (raw) {
          const testUser = JSON.parse(raw);
          setUser(testUser);
          setFirebaseUser({
            getIdToken: async () =>
              sessionStorage.getItem("e2e_test_token") || "fake-token",
          });
          setLoading(false);
          return;
        }
      } catch (e) {
        /* fall through to real auth */
      }
    }
  }, []);

  // Get Firebase ID token
  const getIdToken = async (forceRefresh = false) => {
    if (!firebaseUser) {
      throw new Error("No authenticated user");
    }

    try {
      return await firebaseUser.getIdToken(forceRefresh);
    } catch (err) {
      console.error("Error getting ID token:", err);
      setError(err.message);
      throw err;
    }
  };

  // Sign in with Google
  const loginWithGoogle = async () => {
    setLoading(true);
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope("email");
      provider.addScope("profile");

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      let userEmail = user.email;
      if (!userEmail && user.providerData && user.providerData.length > 0) {
        const googleProvider = user.providerData.find(
          (p) => p.providerId === "google.com"
        );
        if (googleProvider) userEmail = googleProvider.email;
      }

      const userData = {
        uid: user.uid,
        email: userEmail,
        displayName: user.displayName,
        photoURL: user.photoURL,
        phoneNumber: user.phoneNumber,
      };

      sessionStorage.setItem("authUser", JSON.stringify(userData));
      setUser(userData);

      return user;
    } catch (err) {
      console.error("Google sign-in error:", err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Sign in with Apple
  const loginWithApple = async () => {
    setLoading(true);
    setError(null);
    try {
      const provider = new OAuthProvider("apple.com");
      const result = await signInWithPopup(auth, provider);

      // Store user data in sessionStorage
      sessionStorage.setItem(
        "authUser",
        JSON.stringify({
          uid: result.user.uid,
          email: result.user.email,
          displayName: result.user.displayName,
          photoURL: result.user.photoURL,
        })
      );

      return result.user;
    } catch (err) {
      console.error("Apple sign-in error:", err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const setupRecaptcha = (elementOrId) => {
    try {
      // Check if auth is properly initialized
      if (!auth) {
        console.error("Firebase auth not initialized");
        throw new Error("Firebase auth not initialized");
      }

      // Handle both element references and string IDs
      const container =
        typeof elementOrId === "string"
          ? document.getElementById(elementOrId)
          : elementOrId;

      if (!container) {
        throw new Error(`Recaptcha container not found: ${elementOrId}`);
      }

      // Clear any existing instance
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {
          console.warn("Failed to clear existing recaptcha:", e);
        }
      }

      // Create a new instance with proper error handling
      window.recaptchaVerifier = new RecaptchaVerifier(
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => {
            console.log("reCAPTCHA solved");
          },
          "expired-callback": () => {
            console.log("reCAPTCHA expired");
          },
        },
        auth
      );

      return window.recaptchaVerifier;
    } catch (error) {
      console.error("Error setting up recaptcha:", error);
      throw error;
    }
  };
  // Send SMS verification code
  const sendSmsCode = async (phoneNumber, recaptchaElementOrId) => {
    setLoading(true);
    setError(null);
    try {
      const appVerifier = setupRecaptcha(recaptchaElementOrId);
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        appVerifier
      );
      window.confirmationResult = confirmationResult;
      return true;
    } catch (err) {
      console.error("SMS send error:", err);
      setError(err.message);
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Verify SMS code
  const verifySmsCode = async (code) => {
    setLoading(true);
    setError(null);
    try {
      const result = await window.confirmationResult.confirm(code);

      // Store user data in sessionStorage
      sessionStorage.setItem(
        "authUser",
        JSON.stringify({
          uid: result.user.uid,
          phoneNumber: result.user.phoneNumber,
        })
      );

      return result.user;
    } catch (err) {
      console.error("SMS verification error:", err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const logout = async () => {
    try {
      await signOut(auth);
      // Clear user from sessionStorage
      sessionStorage.removeItem("authUser");
      setUser(null);
    } catch (err) {
      console.error("Sign out error:", err);
      setError(err.message);
    }
  };

  // Reset recaptcha
  const resetRecaptcha = () => {
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
      window.recaptchaVerifier = null;
    }
  };

  // Subscribe to auth state changes and check sessionStorage on init
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUserRaw) => {
      if (firebaseUserRaw) {
        setFirebaseUser(firebaseUserRaw);

        // Get existing data from Session Storage
        let storedData = {};
        try {
          const raw = sessionStorage.getItem("authUser");
          if (raw) storedData = JSON.parse(raw);
        } catch (e) {
          console.warn("Failed to parse auth session data", e);
        }

        // Resolve Email:
        // Priority 1: Direct from Firebase
        // Priority 2: From Provider Data (Google/Apple)
        // Priority 3: Recover from Session Storage (The Fix!)
        let finalEmail = firebaseUserRaw.email;

        if (!finalEmail && firebaseUserRaw.providerData) {
          const providerWithEmail = firebaseUserRaw.providerData.find(
            (p) => p.email
          );
          if (providerWithEmail) finalEmail = providerWithEmail.email;
        }

        // If email is still null, but we have it in storage for this exact user, restore it
        if (
          !finalEmail &&
          storedData.uid === firebaseUserRaw.uid &&
          storedData.email
        ) {
          console.log(
            "Restoring email from session storage...",
            storedData.email
          );
          finalEmail = storedData.email;
        }

        // Resolve Provider Data (Don't lose it on refresh)
        let finalProviders = firebaseUserRaw.providerData;
        if (
          (!finalProviders || finalProviders.length === 0) &&
          storedData.uid === firebaseUserRaw.uid
        ) {
          finalProviders = storedData.providerData || [];
        }

        // Construct the Safe User Object
        const userData = {
          uid: firebaseUserRaw.uid,
          email: finalEmail,
          // Merge phone too
          phoneNumber:
            firebaseUserRaw.phoneNumber ||
            (storedData.uid === firebaseUserRaw.uid
              ? storedData.phoneNumber
              : null),
          displayName: firebaseUserRaw.displayName,
          photoURL: firebaseUserRaw.photoURL,
          providerData: finalProviders,
        };

        // Update State & Storage
        setUser(userData);
        sessionStorage.setItem("authUser", JSON.stringify(userData));
      } else {
        setUser(null);
        setFirebaseUser(null);
      }
      setLoading(false);
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, [auth]);

  // Sign in with email and password
  const loginWithEmail = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (error) {
      console.error("Email login error:", error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Create user with email and password
  const signUpWithEmail = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const result = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      return result.user;
    } catch (error) {
      console.error("Email signup error:", error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update user password
  const updateUserPassword = async (newPassword) => {
    if (!firebaseUser) {
      throw new Error("No authenticated user");
    }

    try {
      await updatePassword(firebaseUser, newPassword);
      return true;
    } catch (error) {
      console.error("Password update error:", error);
      setError(error.message);
      throw error;
    }
  };

  // Check if email exists
  const checkEmailExists = async (email) => {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      return methods.length > 0;
    } catch (error) {
      console.error("Error checking email:", error);
      return false;
    }
  };

  // Send password reset email
  const sendPasswordReset = async (email) => {
    setLoading(true);
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      console.error("Password reset error:", error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Instant login: sign in silently with a Firebase custom token minted by the backend
  // (used by the email auto-login fallback — no email verification link).
  const loginWithCustomToken = async (token) => {
    setLoading(true);
    setError(null);
    try {
      const result = await signInWithCustomToken(auth, token);
      const userData = {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        phoneNumber: result.user.phoneNumber,
      };
      sessionStorage.setItem("authUser", JSON.stringify(userData));
      setUser(userData);
      return result.user;
    } catch (err) {
      console.error("Custom token sign-in error:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Send Passwordless Login Link
  const sendPasswordlessLink = async (email) => {
    setLoading(true);
    setError(null);
    try {
      const actionCodeSettings = {
        // URL you want to redirect back to. The domain (www.example.com) for this
        // URL must be in the authorized domains list in the Firebase Console.
        url: window.location.href, // Redirect back to the current page
        handleCodeInApp: true,
      };

      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      
      // Save the email locally so we don't need to ask for it again if they open the link on the same device
      window.localStorage.setItem('emailForSignIn', email);
      
      return true;
    } catch (error) {
      console.error("Error sending passwordless link:", error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Check if current URL is a sign-in link
  const isPasswordlessLink = (url) => {
    return isSignInWithEmailLink(auth, url);
  };

  // Complete the login
  const completePasswordlessLogin = async (emailInput = null) => {
    setLoading(true);
    setError(null);
    try {
      if (!isSignInWithEmailLink(auth, window.location.href)) {
        return false;
      }

      let email = emailInput;
      if (!email) {
        email = window.localStorage.getItem('emailForSignIn');
      }

      // If email is still missing, the user might have opened the link on a different device
      // The UI should handle this by prompting for email if this function returns 'MISSING_EMAIL'
      if (!email) {
        throw new Error('MISSING_EMAIL');
      }

      const result = await signInWithEmailLink(auth, email, window.location.href);
      
      // Clear email from storage
      window.localStorage.removeItem('emailForSignIn');
      
      return result.user;
    } catch (error) {
      console.error("Error completing passwordless login:", error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    firebaseUser,
    getIdToken,
    loading,
    error,
    loginWithGoogle,
    loginWithApple,
    loginWithEmail,
    signUpWithEmail,
    updateUserPassword,
    checkEmailExists,
    sendSmsCode,
    verifySmsCode,
    logout,
    resetRecaptcha,
    sendPasswordReset,
    loginWithCustomToken,
    sendPasswordlessLink,
    completePasswordlessLogin,
    isPasswordlessLink,
  };
}
